import PresenceTotals, {
    MONTH_OPTIONS,
    YEAR_OPTIONS,
} from '../personas/Dashboard/PresenceTotals.jsx'
import PresenceYearTotals from '../personas/Dashboard/PresenceYearTotals.jsx'

// AI-CONTEXT: See docs/features/livereport-widgets-housevisits-personas.md
// Shared "personas presence" visualisation. Used by both
// `DashboardPersonasCard` (dashboard) and the live-report
// `PersonasPresenceWidget`. Caller owns the data + the
// month/year state; this component only renders the percentage line and
// the totals breakdown.
//
// Month/year selects are rendered as a slot — caller passes change
// handlers to show them, or omits them (widget mode) to render a
// read-only header. Filter bar is NOT part of this component (lives in
// the dashboard wrapper; widget surfaces its filter via the sidebar).
//
// Twee weergaven delen deze kop. De maandweergave zet deelnemers tegen de
// dagen van één maand; de jaarweergave zet ze tegen de twaalf maanden. Wie
// een jaar wil verantwoorden moest anders twaalf keer wisselen en zelf
// optellen. De knoppen verschijnen alleen als de aanroeper kan wisselen — in
// een gepubliceerd rapport staat de periode vast.

/** De twee weergaven, in de volgorde waarin de knoppen staan. */
const VIEW_OPTIONS = [
    { value: 'month', label: 'Maand' },
    { value: 'year', label: 'Jaar' },
]

/**
 * @param {object} props
 * @param {number} props.attendancePercentage
 * @param {Array<object>} props.personas
 * @param {number} props.month - 1-12
 * @param {number} props.year - 4-digit year
 * @param {Array<object>} props.presenceReports - Rapporten van de gekozen maand
 * @param {(month: number) => void} [props.onMonthChange] - dashboard passes this; widget omits
 * @param {(year: number) => void} [props.onYearChange] - dashboard passes this; widget omits
 * @param {'month'|'year'} [props.view] - Welke weergave getoond wordt
 * @param {(view: 'month'|'year') => void} [props.onViewChange] - Zonder deze
 *   functie verschijnen de knoppen niet; de weergave ligt dan vast.
 * @param {Array<object>} [props.yearReports] - Rapporten van het hele jaar,
 *   alleen nodig in de jaarweergave
 * @param {boolean} [props.yearLoaded] - Of die jaarrapporten binnen zijn
 * @param {Error|null} [props.error] - Een mislukte read; de jaarweergave meldt
 *   die zelf, zodat hij niet als 'nog bezig' blijft lezen
 * @param {React.ReactNode} [props.emptyState] - When set, rendered instead of the totals
 *   breakdown (dashboard empty/filter-empty state). Header + month/year controls stay
 *   visible so the user can switch to a period that has data.
 * @returns {JSX.Element}
 */
const PersonasPresenceVisualization = ({
    attendancePercentage,
    personas,
    month,
    year,
    presenceReports,
    onMonthChange,
    onYearChange,
    view = 'month',
    onViewChange,
    yearReports = [],
    yearLoaded = false,
    error = null,
    emptyState = null,
}) => {
    const isYear = view === 'year'
    // In de jaarweergave staan de twaalf maanden al naast elkaar; een
    // maandkeuze die er dan bij blijft staan doet niets en belooft van wel.
    const showMonthSelect = Boolean(onMonthChange) && !isYear
    const showYearSelect = Boolean(onYearChange)
    const showControls = showMonthSelect || showYearSelect

    return (
        <>
            <div className="dashboard-personas-card-title-row">
                <p className="dashboard-personas-card-title">
                    {attendancePercentage}% aanwezigheid deze periode
                </p>
                {(showControls || onViewChange) && (
                    <span className="dashboard-personas-card-title-row-controls">
                        {onViewChange && (
                            <span className="presence-view-toggle">
                                {VIEW_OPTIONS.map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        className={`presence-view-toggle__button${
                                            view === option.value ? ' presence-view-toggle__button--active' : ''
                                        }`}
                                        aria-pressed={view === option.value}
                                        onClick={() => onViewChange(option.value)}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </span>
                        )}
                        {showMonthSelect && (
                            <select
                                className="dashboard-personas-card-select"
                                value={month}
                                onChange={(e) => onMonthChange(parseInt(e.target.value, 10))}
                            >
                                {MONTH_OPTIONS.map((label, index) => (
                                    <option key={label} value={index + 1}>{label}</option>
                                ))}
                            </select>
                        )}
                        {showYearSelect && (
                            <select
                                className="dashboard-personas-card-select"
                                value={year}
                                onChange={(e) => onYearChange(parseInt(e.target.value, 10))}
                            >
                                {YEAR_OPTIONS.map((yr) => (
                                    <option key={yr} value={yr}>{yr}</option>
                                ))}
                            </select>
                        )}
                    </span>
                )}
            </div>
            {emptyState || (isYear ? (
                <PresenceYearTotals
                    personas={personas}
                    presenceReports={yearReports}
                    year={year}
                    loaded={yearLoaded}
                    error={error}
                />
            ) : (
                <PresenceTotals
                    personas={personas}
                    month={month}
                    year={year}
                    presenceReports={presenceReports}
                />
            ))}
        </>
    )
}

export default PersonasPresenceVisualization
export { VIEW_OPTIONS }
